import { createFileRoute, Link } from "@tanstack/react-router";
import { validateTicketsSearch } from "@/routes/dashboard/guilds/$applicationGuildId/tickets/index";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  ArrowLeft,
  FileText,
  Hash,
  Bot,
  AlertTriangle,
  CheckCircle,
  ListChecks,
  Mail,
} from "lucide-react";
import { motion, MotionConfig } from "motion/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  TicketDetail,
  TicketLiveChat,
  STATUS_COLORS,
  STATUS_LABELS,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
} from "@/components/tickets";
import { TicketDetailSkeleton } from "@/components/tickets/ticket-detail-skeleton";
import { useQueries } from "@tanstack/react-query";
import { FeatureLock } from "@/components/subscription/feature-lock";
import { ticketQueryOptions, subscriptionQueryOptions } from "@/lib/api";
import { useEligibleStaff, type TicketParticipant } from "@/lib/api/tickets";
import { useGuildRoute } from "@/hooks/use-guild-route";
import { useMemo } from "react";

export const Route = createFileRoute(
  "/dashboard/guilds/$applicationGuildId/tickets/$ticketId"
)({
  component: TicketDetailPage,
  validateSearch: (search: Record<string, unknown>) =>
    validateTicketsSearch(search, {}),
});

function TicketDetailPage() {
  const { applicationGuildId, ticketId } = Route.useParams();
  const { tenantId } = useGuildRoute();
  const listSearch = Route.useSearch();
  const ticketIdNum = parseInt(ticketId, 10);

  const [
    { data: ticketData, isPending: ticketPending },
    { data: subscription },
  ] = useQueries({
    queries: [
      ticketQueryOptions(applicationGuildId, ticketIdNum, { polling: false }),
      subscriptionQueryOptions(tenantId!),
    ],
  });
  const features = subscription?.data?.features;
  const ticket = ticketData?.data;
  usePageTitle(ticket ? `Ticket #${ticket.ticketNumber}` : "Ticket");

  const isClosed = ticket?.status === "closed" || ticket?.status === "resolved";

  const { data: eligibleStaffData } = useEligibleStaff(
    applicationGuildId,
    ticketIdNum
  );

  const mentionableUsers = useMemo<TicketParticipant[]>(() => {
    const users: TicketParticipant[] = [];
    const seen = new Set<string>();

    if (eligibleStaffData?.data) {
      for (const staff of eligibleStaffData.data) {
        seen.add(staff.discordId);
        users.push({
          authorDiscordId: staff.discordId,
          authorUsername: staff.username,
          authorAvatar: staff.avatar,
          authorIsStaff: true,
        });
      }
    }

    if (ticket && !seen.has(ticket.creatorDiscordId)) {
      users.push({
        authorDiscordId: ticket.creatorDiscordId,
        authorUsername: ticket.creatorUsername,
        authorAvatar: ticket.creatorAvatar ?? null,
        authorIsStaff: false,
      });
    }

    return users;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute when creator identity or staff list changes
  }, [
    eligibleStaffData,
    ticket?.creatorDiscordId,
    ticket?.creatorUsername,
    ticket?.creatorAvatar,
  ]);

  if (ticketPending) {
    return <TicketDetailSkeleton />;
  }

  if (!ticket) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link
            to="/dashboard/guilds/$applicationGuildId/tickets"
            params={{ applicationGuildId }}
            search={listSearch}
          >
            <ArrowLeft className="mr-2 size-4" />
            Back to Tickets
          </Link>
        </Button>
        <div className="text-muted-foreground flex min-h-[200px] items-center justify-center">
          Ticket not found
        </div>
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="flex flex-wrap items-center gap-3"
        >
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            aria-label="Back to tickets"
            asChild
          >
            <Link
              to="/dashboard/guilds/$applicationGuildId/tickets"
              params={{ applicationGuildId }}
              search={listSearch}
            >
              <ArrowLeft className="size-4" />
            </Link>
          </Button>

          <div className="flex min-w-0 items-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500/10 to-red-500/10">
              {ticket.ticketType === "modmail" ? (
                <Mail className="size-4 text-purple-500" />
              ) : (
                <Hash className="size-4 text-rose-500" />
              )}
            </div>
            <h1 className="truncate text-lg font-semibold">
              {ticket.subject || `Ticket #${ticket.ticketNumber}`}
            </h1>
            <span className="text-muted-foreground shrink-0 text-sm">
              #{ticket.ticketNumber}
            </span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {ticket.transcriptPublicId ? (
              <FeatureLock
                locked={!features?.ticketTranscripts}
                label="Unlock with Pro"
                compact
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs"
                  asChild
                >
                  <Link
                    to="/transcripts/$transcriptId"
                    params={{ transcriptId: ticket.transcriptPublicId }}
                    target="_blank"
                  >
                    <FileText className="size-3.5" />
                    Transcript
                  </Link>
                </Button>
              </FeatureLock>
            ) : !features?.ticketTranscripts && isClosed ? (
              <FeatureLock locked compact label="Unlock with Pro">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs"
                  disabled
                >
                  <FileText className="size-3.5" />
                  Transcript
                </Button>
              </FeatureLock>
            ) : null}
            <Badge variant="outline" className={STATUS_COLORS[ticket.status]}>
              {STATUS_LABELS[ticket.status]}
            </Badge>
            <Badge
              variant="outline"
              className={PRIORITY_COLORS[ticket.priority]}
            >
              {PRIORITY_LABELS[ticket.priority]}
            </Badge>
          </div>
        </motion.div>

        {/* AI Summary */}
        {ticket.aiSummary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
          >
            <Card>
              <CardContent className="space-y-4 p-5">
                <div className="flex items-center gap-2">
                  <div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500/10 to-red-500/10">
                    <Bot className="size-4 text-rose-500" />
                  </div>
                  <span className="text-sm font-medium">AI Summary</span>
                </div>

                <p className="text-muted-foreground text-sm leading-relaxed">
                  {ticket.aiSummary.summary}
                </p>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <AlertTriangle className="size-3.5 text-rose-500" />
                      <p className="text-muted-foreground text-xs font-medium">
                        Problem
                      </p>
                    </div>
                    <p className="text-sm">{ticket.aiSummary.problem}</p>
                  </div>

                  {ticket.aiSummary.resolution && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <CheckCircle className="size-3.5 text-emerald-500" />
                        <p className="text-muted-foreground text-xs font-medium">
                          Resolution
                        </p>
                      </div>
                      <p className="text-sm">{ticket.aiSummary.resolution}</p>
                    </div>
                  )}
                </div>

                {ticket.aiSummary.keyPoints.length > 0 && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <ListChecks className="size-3.5 text-rose-500" />
                      <p className="text-muted-foreground text-xs font-medium">
                        Key Points
                      </p>
                    </div>
                    <ul className="grid gap-1 sm:grid-cols-2">
                      {ticket.aiSummary.keyPoints.map((point, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-1.5 text-sm"
                        >
                          <span className="mt-1.5 size-1 shrink-0 rounded-full bg-rose-500" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Content: chat + sidebar */}
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <TicketLiveChat
            applicationGuildId={applicationGuildId}
            ticketId={ticketIdNum}
            ticketNumber={ticket.ticketNumber}
            isClosed={isClosed}
            isReadOnly={false}
            isModmail={ticket.ticketType === "modmail"}
            subStatus={ticket.subStatus}
            participants={mentionableUsers}
            className="h-[calc(100dvh-180px)] min-h-[500px]"
          />

          <TicketDetail
            applicationGuildId={applicationGuildId}
            ticket={ticket}
          />
        </div>
      </div>
    </MotionConfig>
  );
}
